import React from 'react';
import { t } from 'i18n-js';
import { tv } from 'tailwind-variants';
import useFilters from '@/hooks/useFilters';
import useFlags from '@/hooks/useFlags';
import AlphabetFilter from '@/components/ui/AlphabetFilter';
import IReferenceMaterial from '@/components/interfaces/ReferenceMaterial';
import { useAlphabetFilter } from '@/components/ui/AlphabetFilter/AlphabetFilter';
import Sidebar, { SidebarHeadWithButton } from '@/components/ui/Sidebar';
import ReferenceMaterialCard from './ReferenceMaterialCard';

interface Props {
  currentStafferId?: number;
  defaultLessonId: number;
  programId: number;
  referenceMaterials: IReferenceMaterial[];
}

// Stack the filter sidebar above the cards below reflow-narrow (37.5rem); side-by-side above.
const layoutStyles = tv({
  base: 'tw-flex tw-gap-8',
  variants: {
    reflow: {
      true: 'tw-flex-col reflow-narrow:tw-flex-row',
    },
  },
});

// tw-top-4 overrides ui/Sidebar's tw-top-14 sticky offset, matching the library FilterPanel.
const sidebarStyles = tv({
  base: 'tw-bg-white tw-top-4',
  variants: {
    reflow: {
      true: 'tw-w-full tw-basis-auto reflow-narrow:tw-w-48 reflow-narrow:tw-basis-48',
    },
  },
});

const Biographies = ({
  currentStafferId, programId, referenceMaterials: initialReferenceMaterials, defaultLessonId
}: Props) => {
  const { activeLetter, availableLetters, data: referenceMaterials, setActiveLetter } = useAlphabetFilter(
    initialReferenceMaterials,
    referenceMaterial => referenceMaterial.title.at(0),
  );

  const { activeFilters, clear, data: filteredData, toggle } = useFilters(referenceMaterials, {
    has_video: {
      getValue: referenceMaterial => referenceMaterial.has_video,
    },
  });

  const { accessibilityReflow } = useFlags();

  return (
    <div>
      <div className="tw-mt-8 tw-mb-4 tw-bg-white">
        <div className="tw-border-b tw-border-solid tw-border-gray-300" />
        <AlphabetFilter activeLetters={availableLetters} initialLetter={activeLetter} onChange={setActiveLetter} />
        <div className="tw-border-t tw-border-dotted tw-border-gray-300" />
      </div>
      <div className={layoutStyles({ reflow: accessibilityReflow })}>
        <Sidebar isFloating className={sidebarStyles({ reflow: accessibilityReflow })}>
          <SidebarHeadWithButton buttonText={t('clear')} headerText={t('filters')} onClick={clear} />
          <label className="tw-flex tw-items-center tw-gap-1 tw-cursor-pointer">
            <input
              className="tw-cursor-pointer"
              type="checkbox"
              checked={activeFilters.has_video}
              onChange={() => toggle('has_video')}
            />
            {t('contains_video')}
          </label>
        </Sidebar>
        <div className="tw-flex tw-flex-wrap tw-gap-4">
          {filteredData.map(referenceMaterial => (
            <ReferenceMaterialCard
              key={referenceMaterial.id}
              currentStafferId={currentStafferId}
              programId={programId}
              referenceMaterial={referenceMaterial}
              lessonId={defaultLessonId}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Biographies;
